import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classService from '../services/classService';
import './EnrollStudent.css';
import Toast from '../../../components/Toast';
import { setStudents, setSelectedStudentIds, toggleStudentSelection, setEnrollmentData, clearEnrollmentData } from '../../../redux/slices/classSlice';

const EnrollStudent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const classId = searchParams.get('classId');
  
  const { user } = useSelector((state) => state.auth);
  const { selectedClass, selectedStudentIds } = useSelector((state) => state.class);
  
  const [students, setLocalStudents] = useState([]);
  const [registeredStudents, setRegisteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    relationship: '',
  });
  const [formErrors, setFormErrors] = useState({
    firstName: false,
    lastName: false,
    dateOfBirth: false,
    gender: false,
    relationship: false,
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Get user ID from auth data
  const userId = user?.userId || user?.user_Id || user?.data?.userId || user?.data?.user_Id;
  
  // Debug logging
  console.log('Auth user object:', user);
  console.log('Extracted userId:', userId);
  console.log('Active classId:', classId, 'Selected class:', selectedClass);
  
  // Get class ID from URL or Redux
  const activeClassId = classId || selectedClass?.class_Id;

  // Prevent duplicate API calls
  const fetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const lastClassIdRef = useRef(null);

  useEffect(() => {
    // Reset fetching refs when user or class changes
    if (!userId || !activeClassId) {
      fetchingRef.current = false;
      hasFetchedRef.current = false;
      setLoading(false);
      return;
    }

    // Clear selections if navigating to a different class
    if (lastClassIdRef.current !== null && lastClassIdRef.current !== activeClassId) {
      dispatch(setSelectedStudentIds([]));
      dispatch(setEnrollmentData(null));
      // Reset fetching refs to allow re-fetching for new class
      fetchingRef.current = false;
      hasFetchedRef.current = false;
    }
    lastClassIdRef.current = activeClassId;

    if (!fetchingRef.current && !hasFetchedRef.current) {
      fetchingRef.current = true;
      hasFetchedRef.current = true;
      fetchStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, activeClassId]);

  // Refresh student list when page becomes visible or gains focus (e.g., after completing payment)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userId && activeClassId) {
        // Reset refs and refetch when page becomes visible
        fetchingRef.current = false;
        hasFetchedRef.current = false;
        fetchStudents();
      }
    };

    const handleFocus = () => {
      if (userId && activeClassId) {
        // Reset refs and refetch when window gains focus
        fetchingRef.current = false;
        hasFetchedRef.current = false;
        fetchStudents();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, activeClassId]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      
      // Fetch user's students for this class
      const data = await classService.fetchStudentDetails(userId, activeClassId);
      console.log('Fetched students:', data);
      setLocalStudents(data || []);
      dispatch(setStudents(data || []));  // Store in Redux
      
      // Check which students are already registered for this class
      const registered = await classService.getRegisteredStudents(userId, activeClassId);
      console.log(`=== REGISTERED STUDENTS FOR CLASS ${activeClassId} ===`);
      console.log('Registered students RAW:', registered);
      console.log('Registered students COUNT:', registered?.length);
      if (registered && registered.length > 0) {
        console.log('First registered student sample:', registered[0]);
        console.log('All property names:', Object.keys(registered[0]));
        console.log('All registered student IDs:', registered.map(r => r.studentId || r.Student_Id));
      } else {
        console.log('No registered students for this class');
      }
      setRegisteredStudents(registered || []);
      
      setError(null);
    } catch (err) {
      // Handle 404 as empty student list (new users with no students)
      if (err.response?.status === 404) {
        console.log('No students found (new user) - showing empty state');
        setLocalStudents([]);
        dispatch(setStudents([]));
        setRegisteredStudents([]);
        setError(null);
      } else {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load students. Please try again.';
        setError(errorMessage);
        console.error('Error fetching students:', err);
        console.error('Error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          userId,
          activeClassId
        });
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const handleAddStudent = () => {
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      relationship: '',
    });
    setShowAddModal(true);
  };

  // Check if a student is already registered for this class
  const isStudentRegistered = (studentId) => {
    // API returns camelCase properties: studentId, enrollmentId
    const isRegistered = registeredStudents.some((rs) => {
      const rsStudentId = rs.studentId || rs.Student_Id || rs.student_Id || 0;
      const rsEnrollmentId = rs.enrollmentId || rs.Enrollment_Id || rs.enrollment_Id || 0;
      
      // Match if student IDs match AND enrollment ID exists
      return rsStudentId === studentId && rsEnrollmentId > 0;
    });
    
    return isRegistered;
  };

  const handleToggleStudent = (student) => {
    const studentId = student.student_Id || student.studentId;
    
    // Prevent selecting already registered students
    if (isStudentRegistered(studentId)) {
      setToastMessage('This student is already enrolled in this class');
      setToastType('warning');
      setShowToast(true);
      return;
    }
    
    // Toggle selection in Redux
    dispatch(toggleStudentSelection(studentId));
  };

  const handleEditStudent = (e, student) => {
    e.stopPropagation();
    setSelectedStudent(student);
    
    // Format date of birth to YYYY-MM-DD for input field
    let dob = student.student_Date_Of_Birth || student.dateOfBirth || '';
    if (dob) {
      try {
        const date = new Date(dob);
        dob = date.toISOString().split('T')[0]; // Extract YYYY-MM-DD
      } catch (err) {
        console.error('Error parsing date:', err);
      }
    }
    
    // Convert gender code to full name (M -> Male, F -> Female)
    let gender = student.student_Gender || student.gender || '';
    if (gender === 'M' || gender === 'm') {
      gender = 'Male';
    } else if (gender === 'F' || gender === 'f') {
      gender = 'Female';
    }
    
    setFormData({
      firstName: student.student_First_Name || student.firstName || '',
      lastName: student.student_Last_Name || student.lastName || '',
      dateOfBirth: dob,
      gender: gender,
      relationship: student.student_Relationship_To_User || student.relationship || '',
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedStudent(null);
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      relationship: '',
    });
    setFormErrors({
      firstName: false,
      lastName: false,
      dateOfBirth: false,
      gender: false,
      relationship: false,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Allow only alphabetic characters and spaces for first/last name
    if (name === 'firstName' || name === 'lastName') {
      const alphaOnly = value.replace(/[^a-zA-Z\s]/g, '');
      setFormData((prev) => ({
        ...prev,
        [name]: alphaOnly,
      }));
      return;
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {
      firstName: !formData.firstName || formData.firstName.trim().length === 0 || formData.firstName.length > 100,
      lastName: !formData.lastName || formData.lastName.trim().length === 0 || formData.lastName.length > 100,
      dateOfBirth: false,
      gender: !formData.gender,
      relationship: !formData.relationship,
    };

    // Validate date of birth - must be between 3 and 99 years old
    if (!formData.dateOfBirth) {
      errors.dateOfBirth = true;
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      
      // Calculate exact age
      let exactAge = age;
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        exactAge--;
      }
      
      // Check if age is less than 3 or greater than 99, or if date is invalid
      if (exactAge < 3 || exactAge > 99 || birthDate > today || birthDate.getFullYear() < 1900) {
        errors.dateOfBirth = true;
      }
    }

    setFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSaveStudent = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      setToastMessage('Please complete all required fields to continue');
      setToastType('warning');
      setShowToast(true);
      return;
    }

    // Validate userId
    if (!userId || userId <= 0) {
      setToastMessage('User ID is missing. Please log in again.');
      setToastType('error');
      setShowToast(true);
      console.error('Invalid userId:', userId, 'User object:', user);
      return;
    }

    // Validate classId
    if (!activeClassId || activeClassId <= 0) {
      setToastMessage('Class ID is missing. Please select a class.');
      setToastType('error');
      setShowToast(true);
      console.error('Invalid classId:', activeClassId);
      return;
    }

    try {
      const studentData = {
        ...formData,
        userId: parseInt(userId),
        classId: parseInt(activeClassId),
      };

      console.log('Saving student with data:', studentData);

      if (showEditModal && selectedStudent) {
        studentData.studentId = selectedStudent.student_Id || selectedStudent.studentId;
        await classService.updateStudent(studentData);
      } else {
        await classService.addStudent(studentData);
      }

      await fetchStudents();
      handleCloseModal();
      
      // Show success toast
      setToastMessage(showEditModal ? 'Student updated successfully!' : 'Student added successfully!');
      setToastType('success');
      setShowToast(true);
    } catch (err) {
      console.error('Error saving student:', err);
      console.error('Error response:', err.response);
      
      // Show error toast
      setToastMessage(`Failed to save student: ${err.response?.data?.error || err.response?.data?.title || err.message || 'Please try again.'}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const getAvatarImage = (gender) => {
    const genderLower = gender?.toLowerCase()?.trim();
    
    if (genderLower === 'male' || genderLower === 'm' || genderLower === 'boy') {
      // Boy avatar - gradient circle with larger emoji
      return `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
          <defs>
            <linearGradient id="boyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#60a5fa;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#3b82f6;stop-opacity:1" />
            </linearGradient>
            <filter id="shadow">
              <feDropShadow dx="0" dy="4" stdDeviation="8" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="100" cy="100" r="95" fill="url(#boyGradient)" filter="url(#shadow)"/>
          <text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👦</text>
        </svg>
      `)}`;
    } else if (genderLower === 'female' || genderLower === 'f' || genderLower === 'girl') {
      // Girl avatar - gradient circle with larger emoji
      return `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
          <defs>
            <linearGradient id="girlGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#f9a8d4;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#ec4899;stop-opacity:1" />
            </linearGradient>
            <filter id="shadow">
              <feDropShadow dx="0" dy="4" stdDeviation="8" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="100" cy="100" r="95" fill="url(#girlGradient)" filter="url(#shadow)"/>
          <text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👧</text>
        </svg>
      `)}`;
    }
    // Default avatar
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
        <defs>
          <filter id="shadow">
            <feDropShadow dx="0" dy="4" stdDeviation="8" flood-opacity="0.3"/>
          </filter>
        </defs>
        <circle cx="100" cy="100" r="95" fill="#f3f4f6" filter="url(#shadow)"/>
        <text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👤</text>
      </svg>
    `)}`;
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNext = () => {
    if (selectedStudentIds.length === 0) {
      setToastMessage('Please click on student tiles to select at least one student');
      setToastType('warning');
      setShowToast(true);
      return;
    }
    
    // Get selected students data
    const selectedStudentsData = students.filter(s => selectedStudentIds.includes(s.student_Id || s.studentId));
    
    // Calculate total fee
    const feePerStudent = selectedClass?.class_Fee || 240;
    const totalFee = selectedStudentsData.length * feePerStudent;
    
    // Store enrollment data in Redux
    dispatch(setEnrollmentData({
      classId: activeClassId,
      selectedStudentIds,
      students: selectedStudentsData,
      totalFee
    }));
    
    // Navigate to order summary (data will be read from Redux)
    navigate('/class/order-summary');
  };

  // Show error if user is not authenticated
  if (!userId || !activeClassId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Please log in to enroll students.</p>
          <button
            onClick={() => navigate('/class/selection')}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-full transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="class-enroll-page min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16">
      {/* Toast Notification - High z-index to appear above modal */}
      {showToast && (
        <div className="fixed top-4 right-4 z-[9999]">
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
            duration={toastType === 'warning' ? 5000 : 3000}
          />
        </div>
      )}
      
      <div className="container-custom">
        {/* Header */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6 mb-8">
            {/* Title Section - Left */}
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
                Select Students to Enroll
              </h1>
              <p className="text-sm text-gray-500">Choose the students you want to enroll in this class</p>
            </div>

            {/* Action Buttons - Right on web, stacked on mobile */}
            <div className="flex flex-wrap gap-3 md:flex-nowrap">
              <button
                onClick={handleBack}
                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-full transition shadow-md hover:shadow-lg flex items-center gap-2"
              >
                ← Back
              </button>
              <button
                onClick={handleAddStudent}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Student
              </button>
              <button
                onClick={handleNext}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2"
              >
                Next
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Students Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Add Attendee Card */}
          <div
            onClick={handleAddStudent}
            className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-dashed border-blue-400 hover:border-purple-500 flex flex-col items-center justify-center min-h-[280px] transform hover:-translate-y-2 hover:scale-105 group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="w-28 h-28 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4 shadow-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                <svg className="w-14 h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <p className="text-gray-800 font-bold text-xl mb-1">Add New Student</p>
              <p className="text-gray-600 text-sm">Click to enroll a student</p>
            </div>
          </div>

          {/* Student Cards */}
          {[...students]
            .sort((a, b) => {
              // Sort: Registered students first, then unregistered
              const aId = a.student_Id || a.studentId;
              const bId = b.student_Id || b.studentId;
              const aRegistered = isStudentRegistered(aId);
              const bRegistered = isStudentRegistered(bId);
              
              if (aRegistered && !bRegistered) return -1;
              if (!aRegistered && bRegistered) return 1;
              
              // Within same group, sort by first name
              const aName = (a.student_First_Name || a.firstName || '').toLowerCase();
              const bName = (b.student_First_Name || b.firstName || '').toLowerCase();
              return aName.localeCompare(bName);
            })
            .map((student) => {
            const studentId = student.student_Id || student.studentId;
            const isSelected = selectedStudentIds.includes(studentId);
            const isRegistered = isStudentRegistered(studentId);
            
            return (
              <div
                key={studentId}
                className={`rounded-3xl p-6 shadow-xl transition-all duration-300 border-2 relative group min-h-[280px] ${
                  isRegistered
                    ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-75'
                    : isSelected 
                    ? 'bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-amber-400 ring-4 ring-amber-300 shadow-amber-200 cursor-pointer hover:shadow-2xl transform hover:-translate-y-2 hover:scale-105' 
                    : 'bg-gradient-to-br from-white via-gray-50 to-blue-50 border-gray-200 hover:border-blue-400 cursor-pointer hover:shadow-2xl transform hover:-translate-y-2 hover:scale-105'
                }`}
                onClick={() => !isRegistered && handleToggleStudent(student)}
              >
                {/* Already Registered Badge */}
                {isRegistered && (
                  <div className="absolute top-3 right-3 z-10">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-semibold text-xs">Registered</span>
                    </div>
                  </div>
                )}

                {/* Selection Checkmark */}
                {isSelected && !isRegistered && (
                  <div className="absolute top-3 right-14 z-10">
                    <div className="bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full p-1.5 shadow-lg animate-scale-in">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Edit Button */}
                {!isRegistered && (
                  <div className="absolute top-3 right-3 z-10">
                    <button
                      type="button"
                      onClick={(e) => handleEditStudent(e, student)}
                      className="bg-blue-500 hover:bg-blue-600 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
                    >
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Gender Badge */}
                <div className="absolute top-3 left-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    (student.student_Gender || student.gender)?.toLowerCase() === 'male' 
                      ? 'bg-blue-100 text-blue-700' 
                      : (student.student_Gender || student.gender)?.toLowerCase() === 'female'
                      ? 'bg-pink-100 text-pink-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {(student.student_Gender || student.gender)?.toLowerCase() === 'male' ? '👦 Boy' : (student.student_Gender || student.gender)?.toLowerCase() === 'female' ? '👧 Girl' : '👤'}
                  </span>
                </div>

                {/* Avatar */}
                <div className="flex justify-center mb-4 mt-8">
                  <div className={`relative rounded-full border-2 ${
                    (student.student_Gender || student.gender)?.toLowerCase() === 'male'
                      ? 'border-blue-400'
                      : (student.student_Gender || student.gender)?.toLowerCase() === 'female'
                      ? 'border-pink-400'
                      : 'border-gray-400'
                  }`}>
                    <img
                      src={getAvatarImage(
                        student.student_Gender || student.gender,
                        `${student.student_First_Name || student.firstName || ''} ${student.student_Last_Name || student.lastName || ''}`
                      )}
                      alt={`${student.student_First_Name || student.firstName} ${student.student_Last_Name || student.lastName}`}
                      className="w-28 h-28 rounded-full"
                      onError={(e) => {
                        e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                            <circle cx="100" cy="100" r="95" fill="#f3f4f6"/>
                            <text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">👤</text>
                          </svg>
                        `)}`;
                      }}
                    />
                  </div>
                </div>

                {/* Student Name */}
                <p className="text-center text-gray-800 font-bold text-lg mb-1">
                  {student.student_First_Name || student.firstName} {student.student_Last_Name || student.lastName}
                </p>
                
                {/* Relationship Badge */}
                {(student.student_Relationship_To_User || student.relationship) && (
                  <p className="text-center text-gray-500 text-sm">
                    {student.student_Relationship_To_User || student.relationship}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Add/Edit Student Modal */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slideInUp">
              {/* Modal Header - Ultra Slick 3D */}
              <div className="bg-gradient-to-b from-white to-gray-50 px-8 py-6 relative" style={{
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
              }}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    {/* Glowing 3D Icon */}
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl blur-xl opacity-60 animate-pulse"></div>
                      <div className="relative bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl p-3 transform hover:scale-110 transition-all duration-300 hover:rotate-6" style={{
                        boxShadow: '0 8px 16px -4px rgba(59, 130, 246, 0.6), 0 4px 8px -2px rgba(147, 51, 234, 0.6), inset 0 2px 4px rgba(255, 255, 255, 0.3)'
                      }}>
                        <svg className="w-7 h-7 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Title with Classic Black Text */}
                    <div>
                      <h2 className="text-2xl font-black text-gray-900 tracking-tight" style={{
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}>
                        {showEditModal ? 'Edit Student' : 'Add New Student'}
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5 font-medium">
                        {showEditModal ? 'Update student information' : 'Enter student details below'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Sleek Close Button */}
                  <button
                    onClick={handleCloseModal}
                    className="group relative text-gray-400 hover:text-gray-700 rounded-xl p-2.5 transition-all duration-300 transform hover:rotate-90 hover:scale-110"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Form */}
              <div className="p-6 space-y-5">
                {/* First Name */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    maxLength="100"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${
                      formErrors.firstName 
                        ? 'border-red-500 focus:ring-red-500 bg-red-50' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200 hover:border-blue-300'
                    }`}
                    required
                  />
                  {formErrors.firstName && (
                    <p className="text-red-500 text-xs mt-1">
                      {formData.firstName.length > 100 ? 'Maximum 100 characters allowed' : 'First name is required'}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    maxLength="100"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${
                      formErrors.lastName 
                        ? 'border-red-500 focus:ring-red-500 bg-red-50' 
                        : 'border-gray-200 focus:border-purple-500 focus:ring-purple-200 hover:border-purple-300'
                    }`}
                    required
                  />
                  {formErrors.lastName && (
                    <p className="text-red-500 text-xs mt-1">
                      {formData.lastName.length > 100 ? 'Maximum 100 characters allowed' : 'Last name is required'}
                    </p>
                  )}
                </div>

                {/* Date of Birth */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Date Of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    min={new Date(new Date().setFullYear(new Date().getFullYear() - 99)).toISOString().split('T')[0]}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 3)).toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${
                      formErrors.dateOfBirth 
                        ? 'border-red-500 focus:ring-red-500 bg-red-50 shadow-[0_2px_0_0_rgb(239,68,68)]' 
                        : 'border-gray-200 focus:border-pink-500 focus:ring-pink-200 hover:border-pink-300'
                    }`}
                    required
                  />
                  {formErrors.dateOfBirth && (
                    <p className="text-red-500 text-xs mt-1 font-semibold">
                      Student must be between 3 and 99 years old
                    </p>
                  )}
                </div>

                {/* Gender */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 appearance-none bg-white ${
                      formErrors.gender 
                        ? 'border-red-500 focus:ring-red-500 bg-red-50' 
                        : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200 hover:border-indigo-300'
                    }`}
                    required
                  >
                    <option value="">Select gender</option>
                    <option value="Male">👦 Male</option>
                    <option value="Female">👧 Female</option>
                  </select>
                </div>

                {/* Relationship */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Relationship <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 appearance-none bg-white ${
                      formErrors.relationship 
                        ? 'border-red-500 focus:ring-red-500 bg-red-50' 
                        : 'border-gray-200 focus:border-green-500 focus:ring-green-200 hover:border-green-300'
                    }`}
                    required
                  >
                    <option value="">Select relationship</option>
                    <option value="Self">🙋 Self</option>
                    <option value="Parent">👨‍👩‍👧 Parent</option>
                    <option value="Legal Guardian">👤 Legal Guardian</option>
                  </select>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="px-6 pb-6 pt-2 flex gap-3">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
                <button
                  onClick={handleSaveStudent}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnrollStudent;
